/** @format */

import {format, isWeekend} from 'date-fns';

import {ru} from 'date-fns/locale';

import {MultilineChartInterface, PointName} from 'components/charts/multiline-chart/model/common';

import {Chart, CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {getIndexFromPointName} from 'helpers/miscHelpers';

import {XAxisData} from 'components/charts/multiline-chart/model/xAxis';

import {WIDGETS_LIST} from '../../constants/widgets';
import {ChartIds} from '../../models/chartIds';
import {WidgetBlock, WidgetBlockId} from '../../models/widgets';
import {AddScatterDataOptions, InitialValues, PreviousPointList, PreviousValues} from './types';

import {
  ALL_BUDGET_CHART_IDS,
  ALL_SENSOR_CHART_IDS,
  ALL_STUB_CHART_IDS,
} from '../../constants/allChartIds';
import {
  HUE_BUDGET,
  HUE_FUNDS,
  HUE_KPI,
  HUE_PROPERTIES,
  HUE_SEASONALITY,
  HUE_SENSOR_GRAY,
  HUE_SENSOR_RED,
  HUE_SENSOR_YELLOW,
  HUE_WEATHER,
} from '../../constants/common';
import {CHARTS_PREDICATES} from '../../constants/predicates';
import getHexByHue from '../../helpers/getHexByHue';
import {MediaplanFromQuery} from '../../models/api';
import getPlanPoint from '../getPlanPoint';
import {ScatterButtonData} from '../../models/scatters';
import {getScatterPointDataLogs} from '../getScatterPointDataLogs';
import {getScatterPointDataNotes} from '../getScatterPointDataNotes';

class MemoryHandler {
  resultCharts: MultilineChartInterface<ChartIds> = {
    xAxisDataList: {},
    charts: [],
    tooltipDataList: {},
  };

  resultWidgetBlocks: WidgetBlock[];

  // Нужно для корректной обработки кумулятивных значений с поля secondary
  private readonly previousPointList: PreviousPointList = {};

  // Нужно для указания месяцев на xAxis
  private needMonth = true;

  constructor(initialValues?: InitialValues) {
    if (initialValues) {
      const {resultCharts, previousPointList} = initialValues;
      this.resultCharts = resultCharts ?? {
        xAxisDataList: {},
        charts: [],
        tooltipDataList: {},
      };
      this.previousPointList = previousPointList ?? {};
    }

    const blocks: WidgetBlock[] = [
      {
        id: 'budget',
        color: {
          main: getHexByHue(HUE_BUDGET),
        },
        commonWidgets: ALL_BUDGET_CHART_IDS,
        order: 0,
      },
      {
        id: 'notes',
        color: {
          main: '#CCA941',
        },
        commonWidgets: ['notes'],
        order: 15,
      },
      {
        id: 'kpi',
        color: {
          main: getHexByHue(HUE_KPI),
        },
        commonWidgets: [],
        label: 'KPI',
        order: 10,
      },
      {
        id: 'properties',
        color: {
          main: getHexByHue(HUE_PROPERTIES),
        },
        commonWidgets: [],
        label: 'Показатели',
        subMenu: {
          addButtonText: '+ Добавить показатель',
          widgets: [],
        },
        order: 20,
      },
      {
        id: 'sensors',
        color: {
          sensorRed: getHexByHue(HUE_SENSOR_RED),
          sensorGray: getHexByHue(HUE_SENSOR_GRAY),
          sensorYellow: getHexByHue(HUE_SENSOR_YELLOW),
        },
        commonWidgets: ALL_SENSOR_CHART_IDS,
        label: 'Сенсоры',
        order: 40,
      },
    ];

    blocks.push({
      id: 'stubs',
      color: {
        stub_weather: getHexByHue(HUE_WEATHER),
        stub_seasonality: getHexByHue(HUE_SEASONALITY),
        stub_price_index: getHexByHue(HUE_FUNDS),
        stub_cb_rate: getHexByHue(HUE_FUNDS),
        stub_currency: getHexByHue(HUE_FUNDS),
      },
      commonWidgets: ALL_STUB_CHART_IDS,
      order: 50,
    });

    blocks.push({
      id: 'funnel',
      color: {
        main: getHexByHue(HUE_PROPERTIES),
      },
      commonWidgets: [],
      label: 'Воронка',
      subMenu: {
        addButtonText: '% Добавить CR',
        widgets: [],
      },
      order: 30,
    });

    blocks.push({
      id: 'scatters',
      color: {
        main: '#8EA8FD',
      },
      commonWidgets: ['logs'],
      order: 5,
    });

    this.resultWidgetBlocks = blocks.toSorted((a, b) => a.order - b.order);
  }

  // Блоки виджетов

  private getWidgetBlockIndexByChartId = (id: WidgetBlockId): number => {
    return this.resultWidgetBlocks.findIndex(d => d.id === id);
  };

  pushWidgetCommonId = (blockId: WidgetBlockId, id: ChartIds) => {
    const index = this.getWidgetBlockIndexByChartId(blockId);
    if (index > -1) {
      const block = this.resultWidgetBlocks[index];

      if (block.subMenu && !block.subMenu.widgets.includes(id)) {
        block.subMenu.widgets.push(id);
      } else {
        block.commonWidgets?.push(id);
      }
    }
  };

  // Разное

  addXAxis = (pointName: PointName, dateString: string, today: string) => {
    const date = new Date(dateString);
    const result: XAxisData = {
      label: format(date, 'd'),
      color: isWeekend(date) ? '#EC8786' : '#FAFBFE',
    };

    const todayLabel = dateString === today ? format(today, 'd') : null;
    if (todayLabel) {
      result.todayLabel = todayLabel;
    }

    const day = date.getDate();
    if (this.needMonth || day === 1) {
      let month = format(date, 'LLLL', {locale: ru});
      result.month = month.slice(0, 1).toUpperCase() + month.slice(1);
      this.needMonth = false;
    }

    this.resultCharts.xAxisDataList[pointName] = result;
  };

  private getChartIndexById = (id: ChartIds) => {
    return this.resultCharts.charts.findIndex(d => d.chartId === id);
  };

  addChartData = (chartId: ChartIds, chartData: CommonChartData) => {
    const chartIndex = this.getChartIndexById(chartId);

    if (chartIndex > -1) {
      this.pushChartData(chartIndex, chartData);
    } else {
      const newChart = this.createNewChart(chartId, chartData);
      this.pushChart(newChart);
    }
  };

  private pushChartData = (index: number, data: CommonChartData) => {
    this.resultCharts.charts[index].chartsData.push(data);
  };

  private updateChartData = (chartIndex: number, dataIndex: number, data: CommonChartData) => {
    this.resultCharts.charts[chartIndex].chartsData[dataIndex] = {
      ...this.resultCharts.charts[chartIndex].chartsData[dataIndex],
      ...data,
    };
  };

  /**
   * Дополняем графики новыми данными:
   * 1. Цветовые схемы из виджетов.
   * 2. Информация о типе графика.
   */

  updateCharts = () => {
    this.resultWidgetBlocks.forEach(block => {
      const {commonWidgets, subMenu} = block;

      const commonWidgetsSafe = commonWidgets ? commonWidgets : [];
      const subMenuSafe = subMenu ? subMenu.widgets : [];
      const allIds = [...commonWidgetsSafe, ...subMenuSafe];

      allIds.forEach(chartId => {
        this.updateChart(chartId, block);
      });
    });
  };

  private updateChart = (chartId: ChartIds, block: WidgetBlock) => {
    const {color, label} = block;

    const chartIndex = this.resultCharts.charts.findIndex(d => d.chartId === chartId);
    if (chartIndex > -1) {
      // Добавить цветовые схемы из виджетов
      if (color.main) {
        this.resultCharts.charts[chartIndex].color = color.main;
      } else if (color[chartId]) {
        this.resultCharts.charts[chartIndex].color = color[chartId];
      }

      // Добавить информацию о типе графика
      this.resultCharts.charts[chartIndex].labelType = label;
    }
  };

  private createNewChart = (chartId: ChartIds, chartData: CommonChartData): Chart<ChartIds> => {
    const {label, hide, token, skipValueLabels} = WIDGETS_LIST[chartId];

    const funnelBlock = this.resultWidgetBlocks.find(block => block.id === 'funnel');
    const isCommonWidget = funnelBlock?.commonWidgets?.includes(chartId);
    const isSubMenuWidget = funnelBlock?.subMenu?.widgets.includes(chartId);

    const isFunnelChart =
      chartId.startsWith('funnel_cr_') || (funnelBlock && (isCommonWidget || isSubMenuWidget));

    const defaultColor = isFunnelChart ? getHexByHue(HUE_PROPERTIES) : '#fff';
    const newChart: Chart<ChartIds> = {
      chartId,
      label: token ? `${label}, ${token}` : label,
      color: defaultColor,
      chartsData: [chartData],
      skipValueLabels,
    };

    if (hide) {
      newChart.hide = hide;
    }

    return newChart;
  };

  private pushChart = (data: Chart<ChartIds>) => {
    this.resultCharts.charts.push(data);
  };

  setPreviousValue = (chartId: ChartIds, key: keyof PreviousValues, value: number) => {
    if (this.previousPointList[chartId]) {
      this.previousPointList[chartId][key] = value;
    } else {
      this.previousPointList[chartId] = {[key]: value};
    }
  };

  getPreviousValue = (chartId: ChartIds, key: keyof PreviousValues): number => {
    if (this.previousPointList[chartId]) {
      return this.previousPointList[chartId][key] ?? 0;
    }
    return 0;
  };

  /**
   * Добавит план в chartData, если он есть
   * @param pointName
   * @param date
   * @param mediaplans
   */

  addPlan = (pointName: PointName, date: string, mediaplans: ReadonlyArray<MediaplanFromQuery>) => {
    for (let i = 0; i < this.resultCharts.charts.length; i++) {
      const chart = this.resultCharts.charts[i];
      const chartId = chart.chartId;

      if (ALL_SENSOR_CHART_IDS.includes(chartId)) {
        // Не добавляем план для сенсоров
        continue;
      }

      const chartData: CommonChartData = {
        name: pointName,
      };

      const planPoint = getPlanPoint({
        currentDate: date,
        mediaplans,
        chartId,
      });

      if (planPoint !== undefined) {
        chartData.line = planPoint;
        const secondaryPredicate = CHARTS_PREDICATES[chartId]?.secondaryPredicate;

        if (secondaryPredicate) {
          // Если у графика есть secondary вариация, значит, может быть и предыдущее secondary значение
          const previous = this.getPreviousValue(chartId, 'lineSecondary');
          chartData.lineSecondary = secondaryPredicate(
            chartData.lineSecondary ?? planPoint,
            previous,
          );
        }

        const chartDataIndex = getIndexFromPointName(pointName);
        this.updateChartData(i, chartDataIndex, chartData);
      }

      // Предыдущее secondary значение теперь текущее
      this.setPreviousValue(chartId, 'lineSecondary', chartData.lineSecondary ?? 0);
    }
  };

  // CR

  addAnalyticGoalChartBySeries = (chartId: ChartIds, points: PointName[], series: number[]) => {
    let chartIndex = this.getChartIndexById(chartId);
    if (chartIndex === -1) {
      // Если по каким-то причинам серия ещё не создана в основном цикле, создаём базовую точку
      const firstPoint: CommonChartData = {
        name: points[0],
        primary: series[0] || 0,
        secondary: series[0] || 0,
      };
      const newChart = this.createNewChart(chartId, firstPoint);
      this.pushChart(newChart);
    }
    // Иначе серия уже построена с поддержкой прогноза — не пересоздаём
  };

  // Скаттеры

  private createNewScatterChart = (
    chartId: ChartIds,
    scatterData: CommonChartData,
  ): Chart<ChartIds> => {
    const {label, hide} = WIDGETS_LIST[chartId];

    const newChart: Chart<ChartIds> = {
      chartId,
      label,
      color: '#fff',
      chartsData: [scatterData],
      isScatter: true,
      skipValueLabels: true,
    };

    if (hide) {
      newChart.hide = hide;
    }

    return newChart;
  };

  addScatterDataLogs = (chartId: ChartIds, pointName: PointName, data?: ScatterButtonData) => {
    const scattersData = getScatterPointDataLogs(pointName, data);
    const index = this.getChartIndexById(chartId);

    if (index > -1) {
      this.pushChartData(index, scattersData);
    } else {
      const newChart = this.createNewScatterChart(chartId, scattersData);
      this.pushChart(newChart);
    }
  };

  addScatterDataNotes = ({chartId, ...options}: AddScatterDataOptions) => {
    const scattersData = getScatterPointDataNotes(options);
    const index = this.getChartIndexById(chartId);

    if (index > -1) {
      this.pushChartData(index, scattersData);
    } else {
      const newChart = this.createNewScatterChart(chartId, scattersData);
      this.pushChart(newChart);
    }
  };
}

export default MemoryHandler;
